import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  OrganizationDto,
  OrganizationService,
  ParkingSpaceDto,
  ParkingSpaceService,
  ParkingTypeDefnitionDto,
  ParkingTypeDto,
  ParkingTypeService,
  ParkingZoneDto,
  ParkingZoneService,
  TariffService,
} from '../../../api/gen';
import {
  AddTariffConditionRequest,
  AddTariffRuleRequest,
  EditTariffRuleRequest,
  CONDITION_KINDS,
  CONDITION_OPERATORS,
  ConditionKind,
  ConditionOperator,
  ParkerClassification,
  RULE_CLASSIFICATIONS,
  RULE_EFFECTS,
  RuleConditionDraft,
  RuleEffect,
  TariffDto,
  TariffRuleDto,
} from '../models/tariff.models';

interface TariffRuleDialogData {
  tariff: TariffDto;
  /** Present when editing an existing rule; absent when creating one. */
  rule?: TariffRuleDto;
}

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-tariff-rule-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatDialogModule, MatStepperModule, MatIconModule, TranslatePipe],
  templateUrl: './tariff-rule-dialog.component.html',
  styleUrl: './tariff-rule-dialog.component.scss',
})
export class TariffRuleDialogComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly ref = inject(MatDialogRef<TariffRuleDialogComponent>);
  private readonly t = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly spaceService = inject(ParkingSpaceService);
  private readonly zoneService = inject(ParkingZoneService);
  private readonly parkingTypeService = inject(ParkingTypeService);
  private readonly orgService = inject(OrganizationService);
  private readonly tariffApi = inject(TariffService);
  readonly data = inject<TariffRuleDialogData>(MAT_DIALOG_DATA);

  // ---- Step 1: Basics ----
  readonly basicsForm = this.fb.group({
    effect: ['Include' as RuleEffect, Validators.required],
    priority: [100, [Validators.required, Validators.min(0)]],
  });
  readonly effects = RULE_EFFECTS;

  // ---- Step 2: Space ----
  readonly spaceSearch = signal('');
  readonly spaces = signal<ParkingSpaceDto[]>([]);
  readonly isLoadingSpaces = signal(false);
  readonly selectedSpaceId = signal('');
  readonly selectedSpace = computed(() => this.spaces().find((s) => s.id === this.selectedSpaceId()) ?? null);
  private spaceSearchTimer: ReturnType<typeof setTimeout> | null = null;

  // ---- Step 3: Zone ----
  readonly zoneSearch = signal('');
  readonly zones = signal<ParkingZoneDto[]>([]);
  readonly isLoadingZones = signal(false);
  readonly selectedZoneId = signal('');
  readonly selectedZone = computed(() => this.zones().find((z) => z.id === this.selectedZoneId()) ?? null);
  private zoneSearchTimer: ReturnType<typeof setTimeout> | null = null;

  /** When scoped to a space, filter that space's (already-fetched) zone list client-side instead
   *  of re-querying the server — a server search would otherwise silently drop the space scope. */
  readonly displayedZones = computed(() => {
    const list = this.zones();
    if (!this.selectedSpaceId()) return list;
    const term = this.zoneSearch().trim().toLowerCase();
    if (!term) return list;
    return list.filter((z) => (z.name ?? '').toLowerCase().includes(term));
  });

  /** Whether a location scope (Space and/or Zone) narrows this rule — decides the Parking Type step's mode. */
  readonly hasLocationScope = computed(() => !!this.selectedSpaceId() || !!this.selectedZoneId());

  // ---- Step 4: Parking Type — category (no location scope) or product (location scope) ----
  readonly typeDefinitions = signal<ParkingTypeDefnitionDto[]>([]);
  readonly isLoadingTypeDefinitions = signal(false);
  readonly selectedTypeDefinitionId = signal('');

  readonly parkingTypes = signal<ParkingTypeDto[]>([]);
  readonly isLoadingParkingTypes = signal(false);
  readonly selectedParkingTypeId = signal('');

  // ---- Step 5: Organization ----
  readonly orgSearch = signal('');
  readonly organizations = signal<OrganizationDto[]>([]);
  readonly isLoadingOrgs = signal(false);
  readonly selectedOrganizationId = signal('');
  private orgSearchTimer: ReturnType<typeof setTimeout> | null = null;

  // ---- Step 6: Classification ----
  readonly classifications = RULE_CLASSIFICATIONS;
  readonly selectedClassification = signal<ParkerClassification | ''>('');

  // ---- Step 7: Advanced (Parking Right override + Conditions) ----
  /** Grant-level override — the most specific (and rarest) selector. No name-search endpoint
   *  exists for ParkingRight today, so this is a plain id field rather than a picker. */
  readonly parkingRightId = signal('');

  readonly conditionKinds = CONDITION_KINDS;
  readonly conditionOperators = CONDITION_OPERATORS;
  readonly conditionDraft = this.fb.group({
    kind: [ConditionKind.DateRange as ConditionKind, Validators.required],
    operator: [ConditionOperator.Equals as ConditionOperator, Validators.required],
    value: ['', Validators.required],
    valueTo: [''],
    negate: [false],
  });
  readonly isBetweenOperator = computed(() => this.conditionDraft.controls.operator.value === ConditionOperator.Between);
  readonly stagedConditions = signal<RuleConditionDraft[]>([]);

  // ---- Summary / submit ----
  readonly isSubmitting = signal(false);
  readonly submitError = signal<string | null>(null);

  readonly hasAnySelector = computed(
    () =>
      !!this.selectedSpaceId() ||
      !!this.selectedZoneId() ||
      !!this.selectedTypeDefinitionId() ||
      !!this.selectedParkingTypeId() ||
      !!this.selectedOrganizationId() ||
      !!this.selectedClassification() ||
      !!this.parkingRightId().trim() ||
      this.stagedConditions().length > 0 ||
      // When editing, the rule may be carried entirely by conditions it already has; those are not
      // re-staged here, so without this the wizard would refuse to save a rule the server accepts.
      (this.data.rule?.conditions?.length ?? 0) > 0,
  );

  readonly isEditMode = computed(() => !!this.data.rule);

  ngOnInit(): void {
    this.prefillFromRule();
    this.loadSpaces();
    this.loadAllZones();
    this.loadTypeDefinitions();
    this.loadOrganizations();
  }

  /**
   * Seeds the wizard from the rule being edited.
   *
   * Runs before the pickers load, which is deliberate: the selection is held as an id, not as an
   * object, so it survives the lists arriving late and the summary can render the saved scope even
   * if a lookup call fails outright.
   */
  private prefillFromRule(): void {
    const rule = this.data.rule;
    if (!rule) return;

    this.basicsForm.patchValue({
      effect: (rule.effect ?? 'Include') as RuleEffect,
      priority: rule.priority ?? 100,
    });

    this.selectedSpaceId.set(rule.spaceId ?? '');
    this.selectedZoneId.set(rule.zoneId ?? '');
    this.selectedTypeDefinitionId.set(rule.typeDefinitionId ?? '');
    this.selectedParkingTypeId.set(rule.parkingTypeId ?? '');
    this.selectedOrganizationId.set(rule.organizationId ?? '');
    this.parkingRightId.set(rule.parkingRightId ?? '');

    // 'Unspecified' is the wire sentinel for "no classification", not a selectable value.
    const classification = rule.classification;
    this.selectedClassification.set(
      classification && classification !== 'Unspecified' ? (classification as ParkerClassification) : '',
    );

    // Existing conditions stay on the rule and are not re-posted; the wizard only stages NEW ones.
    if (this.selectedSpaceId()) this.loadZonesForSpace(this.selectedSpaceId());
    if (this.hasLocationScope()) this.fetchParkingTypesForScope();
  }

  ngOnDestroy(): void {
    if (this.spaceSearchTimer) clearTimeout(this.spaceSearchTimer);
    if (this.zoneSearchTimer) clearTimeout(this.zoneSearchTimer);
    if (this.orgSearchTimer) clearTimeout(this.orgSearchTimer);
  }

  // ---- Space ----

  onSpaceSearchChange(value: string): void {
    this.spaceSearch.set(value);
    if (this.spaceSearchTimer) clearTimeout(this.spaceSearchTimer);
    this.spaceSearchTimer = setTimeout(() => this.loadSpaces(), SEARCH_DEBOUNCE_MS);
  }

  private loadSpaces(): void {
    this.isLoadingSpaces.set(true);
    this.spaceService
      .parkingSpaceFilterPost(this.spaceSearch().trim() || undefined, undefined, undefined, undefined, undefined, undefined, 0, 50)
      .subscribe({
        next: (result) => {
          this.spaces.set(result?.items ?? []);
          this.isLoadingSpaces.set(false);
        },
        error: () => this.isLoadingSpaces.set(false),
      });
  }

  onSpaceSelected(id: string): void {
    this.selectedSpaceId.set(id);
    this.zoneSearch.set('');
    if (id) {
      this.selectedZoneId.set('');
      this.loadZonesForSpace(id);
    } else {
      this.loadAllZones();
    }
    this.reloadParkingTypesForScope();
  }

  // ---- Zone ----

  onZoneSearchChange(value: string): void {
    this.zoneSearch.set(value);
    // Space-scoped: displayedZones() filters client-side, no server round-trip needed.
    if (this.selectedSpaceId()) return;
    if (this.zoneSearchTimer) clearTimeout(this.zoneSearchTimer);
    this.zoneSearchTimer = setTimeout(() => this.loadAllZones(), SEARCH_DEBOUNCE_MS);
  }

  private loadZonesForSpace(spaceId: string): void {
    this.isLoadingZones.set(true);
    this.spaceService.parkingSpaceParkingSpaceIdGet(spaceId).subscribe({
      next: (space) => {
        this.zones.set(space?.parkingZones ?? []);
        this.isLoadingZones.set(false);
      },
      error: () => this.isLoadingZones.set(false),
    });
  }

  private loadAllZones(): void {
    this.isLoadingZones.set(true);
    this.zoneService.parkingZoneFilterPost(this.zoneSearch().trim() || undefined, undefined, undefined, 0, 100).subscribe({
      next: (result) => {
        this.zones.set(result?.items ?? []);
        this.isLoadingZones.set(false);
      },
      error: () => this.isLoadingZones.set(false),
    });
  }

  onZoneSelected(id: string): void {
    this.selectedZoneId.set(id);
    this.reloadParkingTypesForScope();
  }

  // ---- Parking type (category or product) ----

  private loadTypeDefinitions(): void {
    this.isLoadingTypeDefinitions.set(true);
    this.parkingTypeService.parkingTypesGet().subscribe({
      next: (result) => {
        this.typeDefinitions.set(result ?? []);
        this.isLoadingTypeDefinitions.set(false);
      },
      error: () => this.isLoadingTypeDefinitions.set(false),
    });
  }

  /** The user changed the location scope, so the previously chosen product may no longer be offered there. */
  private reloadParkingTypesForScope(): void {
    this.selectedParkingTypeId.set('');
    this.fetchParkingTypesForScope();
  }

  /**
   * Fetches the products available for the current scope WITHOUT touching the selection — the
   * prefill path needs the list to render the saved product's label, and clearing the id there
   * would quietly drop the scope the operator is editing.
   */
  private fetchParkingTypesForScope(): void {
    if (!this.hasLocationScope()) {
      this.parkingTypes.set([]);
      return;
    }
    this.isLoadingParkingTypes.set(true);
    this.parkingTypeService
      .parkingTypesFilterPost({
        availableForParkingSpaces: this.selectedSpaceId() ? [this.selectedSpaceId()] : undefined,
        availableForParkingZones: this.selectedZoneId() ? [this.selectedZoneId()] : undefined,
        pageIndex: 0,
        pageSize: 100,
        includeDeleted: false,
      })
      .subscribe({
        next: (result) => {
          this.parkingTypes.set(result?.items ?? []);
          this.isLoadingParkingTypes.set(false);
        },
        error: () => this.isLoadingParkingTypes.set(false),
      });
  }

  /** "Long-term — Acme Corp" style label; falls back to the type name alone when there's no owner to disambiguate. */
  parkingTypeLabel(t: ParkingTypeDto): string {
    const name = t.type?.name || 'Parking type';
    const owner = t.organization?.name || [t.operator?.firstName, t.operator?.lastName].filter(Boolean).join(' ').trim();
    return owner ? `${name} — ${owner}` : name;
  }

  // ---- Organization ----

  onOrgSearchChange(value: string): void {
    this.orgSearch.set(value);
    if (this.orgSearchTimer) clearTimeout(this.orgSearchTimer);
    this.orgSearchTimer = setTimeout(() => this.loadOrganizations(), SEARCH_DEBOUNCE_MS);
  }

  private loadOrganizations(): void {
    this.isLoadingOrgs.set(true);
    this.orgService
      .organizationsFilterPost({ name: this.orgSearch().trim() || undefined, pageIndex: 0, pageSize: 50, includeDeleted: false })
      .subscribe({
        next: (result) => {
          this.organizations.set(result?.items ?? []);
          this.isLoadingOrgs.set(false);
        },
        error: () => this.isLoadingOrgs.set(false),
      });
  }

  // ---- Advanced: conditions ----

  addCondition(): void {
    if (this.conditionDraft.invalid) {
      this.conditionDraft.markAllAsTouched();
      return;
    }
    const v = this.conditionDraft.getRawValue();
    const draft: RuleConditionDraft = {
      kind: v.kind ?? ConditionKind.DateRange,
      operator: v.operator ?? ConditionOperator.Equals,
      value: (v.value ?? '').trim(),
      valueTo: v.operator === ConditionOperator.Between ? (v.valueTo?.trim() || null) : null,
      negate: !!v.negate,
    };
    this.stagedConditions.update((list) => [...list, draft]);
    this.conditionDraft.reset({ kind: draft.kind, operator: ConditionOperator.Equals, value: '', valueTo: '', negate: false });
  }

  removeCondition(index: number): void {
    this.stagedConditions.update((list) => list.filter((_, i) => i !== index));
  }

  // ---- Summary helpers ----

  get selectedOrganization(): OrganizationDto | null {
    return this.organizations().find((o) => o.id === this.selectedOrganizationId()) ?? null;
  }

  get selectedTypeDefinition(): ParkingTypeDefnitionDto | null {
    return this.typeDefinitions().find((d) => d.id === this.selectedTypeDefinitionId()) ?? null;
  }

  get selectedParkingType(): ParkingTypeDto | null {
    return this.parkingTypes().find((p) => p.id === this.selectedParkingTypeId()) ?? null;
  }

  // ---- Submit ----

  async submit(): Promise<void> {
    if (this.basicsForm.invalid) {
      this.basicsForm.markAllAsTouched();
      return;
    }
    if (!this.hasAnySelector()) {
      this.submitError.set(this.t.instant('tariff.rule.noSelectorsError'));
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set(null);

    const basics = this.basicsForm.getRawValue();

    if (this.data.rule) {
      await this.submitEdit();
      return;
    }

    const request: AddTariffRuleRequest = {
      priority: Number(basics.priority ?? 100),
      effect: (basics.effect ?? 'Include') as RuleEffect,
      zoneId: this.selectedZoneId() || null,
      spaceId: this.selectedSpaceId() || null,
      typeDefinitionId: this.selectedTypeDefinitionId() || null,
      parkingTypeId: this.selectedParkingTypeId() || null,
      organizationId: this.selectedOrganizationId() || null,
      classification: this.selectedClassification() || undefined,
      parkingRightId: this.parkingRightId().trim() || null,
    };

    try {
      const rule = await firstValueFrom(this.tariffApi.tariffsTariffIdRulesPost(this.data.tariff.id!, request));
      const ruleId = rule?.id;
      const conditions = this.stagedConditions();

      if (ruleId && conditions.length > 0) {
        let failures = 0;
        for (const c of conditions) {
          const conditionRequest: AddTariffConditionRequest = {
            kind: c.kind,
            operator: c.operator,
            value: c.value,
            valueTo: c.valueTo,
            negate: c.negate,
          };
          try {
            await firstValueFrom(this.tariffApi.tariffsRulesRuleIdConditionsPost(ruleId, conditionRequest));
          } catch {
            failures++;
          }
        }
        if (failures > 0) {
          this.snackBar.open(
            this.t.instant('tariff.rule.msg.partialConditions', { failed: failures, total: conditions.length }),
            'Close',
            { duration: 6000 },
          );
        }
      }

      this.isSubmitting.set(false);
      this.ref.close(true);
    } catch (err) {
      this.isSubmitting.set(false);
      const detail = (err as { error?: { detail?: string; title?: string } })?.error?.detail
        ?? (err as { error?: { detail?: string; title?: string } })?.error?.title
        ?? this.t.instant('tariff.rule.msg.createFailed');
      this.submitError.set(detail);
    }
  }

  /**
   * Saves an edited rule.
   *
   * Every selector is sent explicitly — a value, or the matching <c>clear</c> flag — because the
   * edit endpoint reads "field omitted" as "leave it alone". Sending only the fields that are set
   * would make the wizard subtractive-only: an operator could add a scope but never remove one, and
   * the dialog would stop being a faithful picture of the rule it is showing.
   */
  private async submitEdit(): Promise<void> {
    const rule = this.data.rule!;
    const basics = this.basicsForm.getRawValue();

    const zoneId = this.selectedZoneId();
    const spaceId = this.selectedSpaceId();
    const typeDefinitionId = this.selectedTypeDefinitionId();
    const parkingTypeId = this.selectedParkingTypeId();
    const organizationId = this.selectedOrganizationId();
    const classification = this.selectedClassification();
    const parkingRightId = this.parkingRightId().trim();

    const request: EditTariffRuleRequest = {
      priority: Number(basics.priority ?? 100),
      effect: (basics.effect ?? 'Include') as RuleEffect,

      zoneId: zoneId || null,
      clearZoneId: !zoneId,
      spaceId: spaceId || null,
      clearSpaceId: !spaceId,
      typeDefinitionId: typeDefinitionId || null,
      clearTypeDefinitionId: !typeDefinitionId,
      parkingTypeId: parkingTypeId || null,
      clearParkingTypeId: !parkingTypeId,
      organizationId: organizationId || null,
      clearOrganizationId: !organizationId,
      classification: classification || undefined,
      clearClassification: !classification,
      parkingRightId: parkingRightId || null,
      clearParkingRightId: !parkingRightId,
    };

    try {
      await firstValueFrom(this.tariffApi.tariffsRulesRuleIdPut(rule.id!, request));

      // Conditions staged during this edit are appended; the rule's existing ones are untouched.
      const conditions = this.stagedConditions();
      let failures = 0;
      for (const c of conditions) {
        const conditionRequest: AddTariffConditionRequest = {
          kind: c.kind,
          operator: c.operator,
          value: c.value,
          valueTo: c.valueTo,
          negate: c.negate,
        };
        try {
          await firstValueFrom(this.tariffApi.tariffsRulesRuleIdConditionsPost(rule.id!, conditionRequest));
        } catch {
          failures++;
        }
      }
      if (failures > 0) {
        this.snackBar.open(
          this.t.instant('tariff.rule.msg.partialConditions', { failed: failures, total: conditions.length }),
          'Close',
          { duration: 6000 },
        );
      }

      this.isSubmitting.set(false);
      this.ref.close(true);
    } catch (err) {
      this.isSubmitting.set(false);
      const detail = (err as { error?: { detail?: string; title?: string } })?.error?.detail
        ?? (err as { error?: { detail?: string; title?: string } })?.error?.title
        ?? this.t.instant('tariff.rule.msg.updateFailed');
      this.submitError.set(detail);
    }
  }

  cancel(): void {
    this.ref.close();
  }
}
